"use client";
import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import CustomInput from "@/components/shared/CustomInput";
import Image from "next/image";
import search from "@/public/images/search.svg";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import TelegramLogin from "@/components/HomeComponents/TelegramLogin";
import AdPannerFooter from "@/components/shared/AdPannerFooter/AdPannerFooter";
import CourseCard from "@/components/shared/CourseCard";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { SelectLoggedUser } from "@/redux/Auth";
import USER, { SelectCourses, SelectMyCourses } from "@/redux/User";
import GENERAL, { SelectSchoolYears } from "@/redux/GENERAL";
import SocialMedia from "@/components/shared/socialMedia/SocialMedia";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import PaginationControls from "@/components/shared/PaginationControls";

const CoursesPage: React.FC = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const loggedUser = useAppSelector(SelectLoggedUser);

  const courses = useAppSelector(SelectCourses);

  const [loading, setLoading] = useState(true);
  const [schoolYearSelected, setSchoolYearSelected] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const schoolYear = useAppSelector(SelectSchoolYears);

  const schoolYearsIDs = searchParams
    .get("schoolyears_ids")
    ?.split("_")
    .map((el) => Number(el));

  useEffect(() => {
    schoolYearsIDs?.map((el) => {
      setSchoolYearSelected((prev) => [...prev, el]);
    });
  }, [schoolYear]);

  useEffect(() => {
    const fetchSchoolYears = async () => {
      const params =
        loggedUser?.isLoggedIn && loggedUser?.userToken
          ? {
              mainEducationType:
                loggedUser?.userData?.details?.type_education?.id,
            }
          : {};

      await dispatch(GENERAL.thunks.doGetSchoolYears(params));
      setLoading(false);
    };

    fetchSchoolYears();
  }, [dispatch, loggedUser]);

  useEffect(() => {
    setLoading(true);
    dispatch(
      USER.thunks.doGetCourses({
        search: searchValue,
        school_years: schoolYearSelected,
        page: currentPage,
        is_digital_book: 1,
      })
    )
      .then((res) => {
        setTotalPages(res.payload.data.total_pages);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [schoolYearSelected, searchValue, currentPage, dispatch]);

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1);
  };

  return (
    <div>
      <Header />
      <div className="container my-10 flex flex-col gap-10">
        <div className="bg-[#323538] rounded-[20px] p-3 flex flex-col gap-5">
          <div className="flex items-center bg-[#474A4D] rounded-[5px] py-2 pl-3">
            <Formik
              initialValues={{ search: "" }}
              onSubmit={(values) => {
                setSearchValue(values.search);
              }}
            >
              {(props) => (
                <Form className="w-full flex justify-between gap-4">
                  <CustomInput
                    type="text"
                    name="search"
                    placeholder="بحث عن كورس في المنصة"
                    fullWidth={true}
                  />
                  <button type="submit">
                    <Image src={search as string} alt="search" />
                  </button>
                </Form>
              )}
            </Formik>
          </div>
          <div className="flex gap-5 flex-wrap">
            {schoolYear.map((year: any, i: number) => (
              <button
                onClick={() => {
                  if (schoolYearSelected.includes(year.id)) {
                    setSchoolYearSelected((prev) =>
                      prev.filter((item) => item !== year.id)
                    );
                    router.push(pathname);
                  } else {
                    setSchoolYearSelected((prev) => [...prev, year.id]);
                    router.push(pathname);
                  }
                }}
                className={
                  schoolYearSelected.includes(year.id)
                    ? "bg-primaryColor text-black"
                    : "bg-[#474A4D] text-white"
                }
                style={{
                  borderRadius: "5px",
                  fontSize: "14px",
                  padding: "12px 20px",
                }}
                key={i}
              >
                {year.name}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <SkeletonTheme baseColor="#474A4D" highlightColor="#6b6e72">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-10">
              {Array(6)
                .fill(0)
                .map((_, index) => (
                  <div key={index} className="p-5 bg-[#323538] rounded-[15px]">
                    <Skeleton height={220} className="w-full" />
                    <Skeleton height={20} width="70%" className="my-3" />
                    <Skeleton height={20} width="90%" />
                    <Skeleton height={20} width="50%" className="mt-3" />
                    <Skeleton height={30} width="100%" className="mt-5" />
                  </div>
                ))}
            </div>
          </SkeletonTheme>
        ) : courses && courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 place-content-center place-items-center">
            {[...courses]?.map((course: any, index: number) => (
              <CourseCard key={index} course={course} eBook={true} />
            ))}
          </div>
        ) : (
          <div className="text-center text-red-400 bg-[#474A4D] rounded-[5px] py-5 px-3">
            <p>لم يتم العثور على دورات.</p>
          </div>
        )}
        <PaginationControls
          currentPage={currentPage}
          totalQuestions={totalPages * 10}
          questionsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>
      <SocialMedia />
      <Footer />
      <TelegramLogin />
      {/* <AdPannerFooter /> */}
    </div>
  );
};

export default CoursesPage;
